import React, { useContext, useMemo, useState } from 'react';
import { appendVendorUrlParams, isExternalUrl } from '../ContentUrl/helper';
import { ContentSchema, RoomStructureContent } from '../../definitions/schemas';
import { ContentTrackerProps } from './Tracking/types';
import { DocumentViewerFacadeProps } from '../DocumentViewer/DocumentViewerFacade';
import { EmbeddableViewerProps } from '../Embeddable/EmbeddableViewer';
import { FullScreenUrlViewerProps } from '../ContentUrl/FullScreenUrlViewer';
import { getContentCover, getTextContentURL } from '../Content/helper';
import { getStorageSyncPreference } from '../../lib/StorageProvider';
import { isVendorMode } from '../../lib/Vendor';
import { Properties } from '../../enums/Analytics';
import { RoomContentContextProps } from '../../lib/RoomContentContext';
import { RoomContentWrapperProps } from './Content/RoomContentWrapper';
import { RoomTextContentProps } from './Content/RoomTextContent';
import { RoomViewContext } from '../Room/View/RoomViewContext';
import { UserActivityEvent } from '../../enums/UserActivity';
import ActivityTracker from '../../Services/Analytics/ActivityTracker';
import ContentType from '../../enums/ContentType';
import DocumentTracker from './Tracking/DocumentTracker';
import DownloadTracker from '../../Services/Analytics/DownloadTracker';
import EmbeddableTracker from './Tracking/EmbeddableTracker';
import RoomAudioContent, {
  RoomAudioContentProps,
} from './Content/RoomAudioContent';
import RoomImageContent, {
  RoomImageContentProps,
} from './Content/RoomImageContent';
import RoomVideoContent, {
  RoomVideoContentProps,
} from './Content/RoomVideoContent';
import TextTracker from './Tracking/TextTracker';
import UrlTracker from './Tracking/UrlTracker';
import { StorageProviderMTPickers } from '../../enums/Integrations/StorageProviders';
import useAppMode from '../../lib/hook/config/useAppMode';
import useFeatureFlag from '../../lib/hook/useFeatureFlag';
import { OrganizationFlagEnum } from '../../lib/features/FeatureFlag';
import { getContentDownloadable } from '../../lib/Content';

type ClassKey = 'url';

export interface RoomContentFacadeProps
  extends ContentTrackerProps,
    Pick<RoomContentWrapperProps, 'width'> {
  actionSpace?: boolean;
  autoPlay?: boolean;
  classes?: Partial<Record<ClassKey, string>>;
  content: Partial<ContentSchema>;
  contentDescription?: string;
  contentName?: string;
  contentUrl?: string;
  contextProps: RoomContentContextProps;
  darkTheme?: boolean;
  disableImageContentName?: boolean;
  downloadTrigger?: number;
  enableDocumentViewportDetection?: boolean;
  hasBorder?: boolean;
  isDownloadable?: boolean;
  referrer: string;
  RoomAudioContentProps?: Partial<RoomAudioContentProps>;
  RoomDocumentContentProps?: Partial<DocumentViewerFacadeProps>;
  RoomEmbeddableContentProps?: Partial<EmbeddableViewerProps>;
  RoomImageContentProps?: Partial<RoomImageContentProps>;
  RoomTextContentProps?: Partial<RoomTextContentProps>;
  RoomURLContentProps?: Partial<FullScreenUrlViewerProps>;
  RoomVideoContentProps?: Partial<RoomVideoContentProps>;
  rounded?: boolean;
  structureContent?: RoomStructureContent;
}

/**
 * Component to display a content in a room, in its "view" mode.
 * This includes tracking for prospects and download & view tracking
 * for internal users (admins/salespeople) viewing one of the rooms
 * in their org.
 */
const RoomContentFacade: React.FC<RoomContentFacadeProps> = (
  props: RoomContentFacadeProps
) => {
  const { isGuestVisitor } = useContext(RoomViewContext);

  const {
    actionSpace,
    autoPlay = false,
    classes,
    content,
    contentDescription,
    contentName,
    contentUrl,
    contextProps,
    darkTheme,
    disableImageContentName = false,
    downloadTrigger,
    enableDocumentViewportDetection = false,
    hasBorder = false,
    isDownloadable,
    referrer,
    RoomAudioContentProps,
    RoomDocumentContentProps,
    RoomEmbeddableContentProps,
    RoomImageContentProps,
    RoomTextContentProps,
    RoomURLContentProps,
    RoomVideoContentProps,
    rounded,
    structureContent,
    width,
    ...contentTrackerProps
  } = props;

  const [viewed, setViewed] = useState(false);

  const { isEmbeddedMode } = useAppMode();

  const trackingProperties = useMemo(
    () => ({
      [Properties.ENTERED_FROM]: referrer,
      [Properties.CONTENT_TYPE]: content.type,
      [Properties.SINGLE_USE]: !content.libraryVisible,
      [Properties.GOOGLE_DRIVE_SYNC]: getStorageSyncPreference(
        content.storageSync?.preference
      ),
    }),
    [content, referrer]
  );

  const isInlineIframeViewerEnabled = useFeatureFlag(
    OrganizationFlagEnum.INLINE_IFRAME_VIEWER
  );
  const isConsentDeclinedSuppression = useFeatureFlag(
    OrganizationFlagEnum.CONSENT_DECLINED_SUPPRESSION
  );

  const [cover, defaultCover, coverFromStructure] = getContentCover(
    content,
    structureContent
  );
  const mediaCover = coverFromStructure || cover || defaultCover;
  // For documents use default cover as primary cover to render the loading state without flickering
  const documentMediaCover = defaultCover || coverFromStructure || cover;

  // Empty fragment is returned for
  // - missing or unknown type
  // - broken media (type and data doesn't match)
  //   @todo server should only return valid contents
  let component = <></>;
  if (!content.type) {
    return component;
  }

  const handleDownload = () => {
    if (content.id) {
      new DownloadTracker(
        content.id,
        props.contactId,
        props.roomId,
        content.version
      ).track(isGuestVisitor);
    }
  };

  const handleView = () => {
    const [, isUrl] = getTextContentURL(content.text?.text);
    // urls will be counted as "viewed" every time the user clicks on them
    if (content.id && (!viewed || isUrl)) {
      setViewed(true);

      // don't track user activity if the user is being treated as a contact
      if (isGuestVisitor) {
        return;
      }

      ActivityTracker.track(UserActivityEvent.ContentViewed, {
        contentId: content.id,
        version: content.version,
      });
    }
  };

  let canDownload = getContentDownloadable(content);
  if (isDownloadable !== undefined) {
    canDownload = isDownloadable;
  }

  switch (content.type) {
    case ContentType.AUDIO:
      if (content.audio) {
        component = (
          <RoomAudioContent
            autoPlay={autoPlay}
            cover={mediaCover}
            downloadable={canDownload}
            onPlay={handleView}
            rounded={rounded}
            url={content.audio.url}
            width={width}
            {...contentTrackerProps}
            {...RoomAudioContentProps}
          />
        );
      }
      break;
    case ContentType.DOCUMENT:
      if (content.document) {
        const { createdAt, document, processingLog } = content;

        component = (
          <DocumentTracker
            contentProcessingLog={processingLog}
            cover={documentMediaCover}
            createdAt={createdAt!}
            darkTheme={darkTheme}
            document={document}
            downloadable={canDownload}
            downloadTrigger={downloadTrigger}
            enableViewportDetection={enableDocumentViewportDetection}
            onDownload={handleDownload}
            onLoad={handleView}
            trackingProperties={trackingProperties}
            {...contentTrackerProps}
            {...RoomDocumentContentProps}
          />
        );
      }
      break;
    case ContentType.EMBEDDABLE:
      if (content.embeddable) {
        const { url } = content.embeddable;
        component = (
          <EmbeddableTracker
            name={content.name || 'unknown source embed code'}
            onView={handleView}
            rounded={rounded}
            url={url}
            {...contentTrackerProps}
            {...RoomEmbeddableContentProps}
          />
        );
      }
      break;
    case ContentType.IMAGE:
      if (content.image) {
        const {
          height,
          predominantColor,
          signedUrl,
          width: imageWidth,
        } = content.image;

        component = (
          <RoomImageContent
            backgroundColor={predominantColor}
            disableContentName={disableImageContentName}
            downloadable={canDownload}
            downloadTrigger={downloadTrigger}
            height={height}
            imageWidth={imageWidth}
            name={contentName}
            onDownload={handleDownload}
            onLoadWithinViewport={handleView}
            rounded={rounded}
            trackingProperties={trackingProperties}
            url={signedUrl}
            {...contentTrackerProps}
            {...RoomImageContentProps}
          />
        );
      }
      break;
    case ContentType.TEXT:
      if (content.text) {
        component = (
          <TextTracker
            cover={mediaCover}
            darkTheme={darkTheme}
            hasBorder={hasBorder}
            html={content.text?.html}
            onView={handleView}
            rounded={rounded}
            text={content.text?.text}
            width={width}
            {...contentTrackerProps}
            {...RoomTextContentProps}
          />
        );
      }
      break;
    case ContentType.URL:
      if (content.url) {
        const { iframe, openGraph, url } = content.url;
        const isAssetHubContent =
          content.mtData?.provider === StorageProviderMTPickers.AssetHub;

        // Allow to take an override from the content structure
        let resourceUrl = contentUrl || url;

        // ternary operation validates vendor mode is enabled, there are context props
        // and url isn't undefined to avoid issues with string methods

        if (
          isVendorMode() &&
          resourceUrl &&
          !isExternalUrl(resourceUrl, content)
        ) {
          resourceUrl = appendVendorUrlParams({
            contextProps,
            url: resourceUrl,
            referrer,
            isDownloadable: structureContent?.isDownloadable && !isEmbeddedMode,
            track: contentTrackerProps.track,
            isInlineIframeViewer:
              isAssetHubContent && isInlineIframeViewerEnabled,
            contentId: content.id || '',
            isConsentDeclinedSuppression,
          });
        }
        const isAssetHubProvider =
          content.mtData?.provider === StorageProviderMTPickers.AssetHub;

        component = (
          <UrlTracker
            rounded={rounded}
            actionSpace={actionSpace}
            isAssetHubProvider={isAssetHubProvider}
            className={classes?.url}
            cover={mediaCover}
            content={content}
            darkTheme={darkTheme}
            description={contentDescription}
            iframe={iframe}
            isExpired={content.expired}
            name={contentName || ''}
            onView={handleView}
            openGraph={openGraph}
            isInlineIframeViewer={isAssetHubContent}
            url={resourceUrl}
            width={width ?? undefined}
            {...contentTrackerProps}
            {...RoomURLContentProps}
          />
        );
      }
      break;
    case ContentType.VIDEO:
      if (content.video) {
        const { captions, downloadUrl, url } = content.video;

        component = (
          <RoomVideoContent
            autoPlay={autoPlay}
            cover={mediaCover}
            downloadable={canDownload}
            downloadUrl={downloadUrl}
            fixedHeight={false}
            onPlay={handleView}
            rounded={rounded}
            captions={captions}
            url={url}
            width={width}
            {...contentTrackerProps}
            {...RoomVideoContentProps}
          />
        );
      }
      break;
  }

  return component;
};

export default RoomContentFacade;
